import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import useStore, { BRAND_COLORS } from '@/store/useStore';
import BottomNav from '@/components/guest/BottomNav';
import EarnSuccessModal from '@/components/guest/EarnSuccessModal';
import RedemptionModal from '@/components/guest/RedemptionModal';
import PwaInstallPrompt from '@/components/guest/PwaInstallPrompt';
import useStoreContext from '@/hooks/useStoreContext';
import { getSelectedMerchantId } from '@/lib/routes';
import { apiGetStoreInfo, apiGetTransactions, apiGetWallet } from '@/services/api';

function hexToRgbString(hex) {
  const safe = (hex || '').replace('#', '');
  if (safe.length !== 6) return '14, 122, 98';
  const n = Number.parseInt(safe, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

function resolveBrandPalette(color) {
  if (String(color || '').toLowerCase() === '#a5f2f3') return BRAND_COLORS.brand;
  if (String(color || '').toLowerCase() === '#5f9ea0') return BRAND_COLORS.brand;
  if (color && BRAND_COLORS[color]) return BRAND_COLORS[color];
  if (typeof color === 'string' && /^#[0-9a-fA-F]{6}$/.test(color)) {
    return { hex: color, start: color, end: color };
  }
  return BRAND_COLORS.brand;
}

function shadeHex(hex, factor) {
  const safe = (hex || '').replace('#', '');
  if (safe.length !== 6) return '#0E7A62';
  let r = parseInt(safe.slice(0, 2), 16);
  let g = parseInt(safe.slice(2, 4), 16);
  let b = parseInt(safe.slice(4, 6), 16);
  if (factor >= 0) {
    r = Math.round(r + (255 - r) * factor);
    g = Math.round(g + (255 - g) * factor);
    b = Math.round(b + (255 - b) * factor);
  } else {
    r = Math.round(r * (1 + factor));
    g = Math.round(g * (1 + factor));
    b = Math.round(b * (1 + factor));
  }
  return `#${Math.max(0, Math.min(255, r)).toString(16).padStart(2, '0')}${Math.max(0, Math.min(255, g)).toString(16).padStart(2, '0')}${Math.max(0, Math.min(255, b)).toString(16).padStart(2, '0')}`;
}

export default function GuestLayout() {
  const {
    guestTheme,
    guestToken,
    merchantInfo,
    setMerchantInfo,
    setWallet,
    setTransactions,
  } = useStore();
  useStoreContext();
  const selectedMerchantId = getSelectedMerchantId();

  useEffect(() => {
    const merchantColor = merchantInfo?.brand_color;
    const palette = resolveBrandPalette(merchantColor);
    const baseHex = palette.hex;
    const lightStart = palette.start || baseHex;
    const lightEnd = palette.end || baseHex;

    const appliedStart = guestTheme === 'dark' ? shadeHex(lightStart, -0.28) : lightStart;
    const appliedEnd = guestTheme === 'dark' ? shadeHex(lightEnd, -0.4) : lightEnd;
    const appliedSolid = guestTheme === 'dark' ? shadeHex(baseHex, -0.34) : baseHex;

    document.documentElement.style.setProperty('--brand-start', appliedStart);
    document.documentElement.style.setProperty('--brand-end', appliedEnd);
    document.documentElement.style.setProperty('--brand-solid', appliedSolid);
    document.documentElement.style.setProperty('--brand-rgb', hexToRgbString(appliedSolid));
  }, [merchantInfo?.brand_color, guestTheme]);

  // Keep guest PWA in sync with backend updates (e.g. merchant credits points)
  useEffect(() => {
    if (!guestToken) return undefined;

    let active = true;
    const sync = async () => {
      if (!selectedMerchantId) return;
      try {
        const [wallet, transactions, storeInfo] = await Promise.all([
          apiGetWallet(),
          apiGetTransactions(),
          apiGetStoreInfo(selectedMerchantId),
        ]);
        if (!active) return;
        setWallet(wallet);
        setTransactions(transactions);
        if (storeInfo?.id) {
          setMerchantInfo(storeInfo);
        }
      } catch (e) {}
    };

    sync();
    const interval = setInterval(sync, 2500);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [guestToken, selectedMerchantId, setMerchantInfo, setTransactions, setWallet]);

  return (
    <div className={guestTheme === 'dark' ? 'dark' : ''}>
      <div className="min-h-screen bg-background text-foreground" data-testid="guest-layout">
        <div className="mx-auto max-w-md min-h-screen pb-20 relative">
          <Outlet />
        </div>
        <BottomNav />
        <EarnSuccessModal />
        <RedemptionModal />
        <PwaInstallPrompt />
      </div>
    </div>
  );
}
